<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', {
      ...basicContactData,
      ...(customAttributes && {
        customAttributes,
      }),
    });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="contact-details">
    <section class="contact-hero">
      <div class="contact-hero-bg">
        <div class="contact-hero-glow" />
        <div class="contact-hero-pattern" />
      </div>
      <div class="contact-hero-inner">
        <div class="contact-hero-left">
          <Avatar
            :src="avatarSrc || ''"
            :name="selectedContact?.name || ''"
            :size="72"
            allow-upload
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
          <div class="min-w-0">
            <h2 class="contact-name">
              {{ selectedContact?.name }}
            </h2>
            <div class="contact-meta">
              <span
                v-if="selectedContact?.identifier"
                class="inline-flex items-center gap-1"
              >
                <span class="i-ph-user-gear size-4 text-n-slate-10" />
                <span class="truncate">{{ selectedContact?.identifier }}</span>
              </span>
              <span class="inline-flex items-center gap-1">
                <span class="i-ph-calendar-dots size-4 text-n-slate-10" />
                {{
                  $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt })
                }}
              </span>
              <span class="dot-sep">•</span>
              <span class="inline-flex items-center gap-1">
                <span class="i-ph-activity size-4 text-n-slate-10" />
                {{
                  $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                    date: lastActivityAt,
                  })
                }}
              </span>
            </div>
            <div class="mt-3">
              <ContactLabels :contact-id="selectedContact?.id" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="contact-card">
      <div class="contact-card-head">
        <div class="min-w-0">
          <h3 class="contact-card-title">
            {{ t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE') }}
          </h3>
          <p class="contact-card-subtitle">
            Update customer details, social links and VIP level.
          </p>
        </div>
        <div class="contact-card-actions">
          <Button
            :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
            size="sm"
            :is-loading="isUpdating"
            :disabled="isUpdating || isFormInvalid"
            @click="updateContact"
          />
        </div>
      </div>

      <div class="contact-card-body">
        <ContactsForm
          ref="contactsFormRef"
          :contact-data="contactData"
          is-details-view
          @update="handleFormUpdate"
        />
      </div>
    </section>

    <section class="danger-card">
      <div class="danger-card-head">
        <div class="min-w-0">
          <h6 class="danger-title">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <p class="danger-subtitle">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
    </section>

    <ConfirmContactDeleteDialog
      ref="confirmDeleteContactDialogRef"
      :selected-contact="selectedContact"
      @go-to-contacts-list="emit('goToContactsList')"
    />
  </div>
</template>

<style scoped>
.contact-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.contact-hero {
  position: relative;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
}

.contact-hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.contact-hero-glow {
  position: absolute;
  top: -60%;
  right: -12%;
  width: 520px;
  height: 520px;
  background: radial-gradient(
    circle,
    rgba(249, 115, 22, 0.12) 0%,
    transparent 65%
  );
}

.contact-hero-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(15, 23, 42, 0.04) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
  opacity: 0.55;
}

.contact-hero-inner {
  position: relative;
  padding: 18px;
}

.contact-hero-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.contact-name {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.contact-meta {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #4b5563;
  font-size: 12px;
}

.dot-sep {
  color: #94a3b8;
}

.contact-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.contact-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.contact-card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}

.contact-card-subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
}

.contact-card-actions {
  flex-shrink: 0;
}

.contact-card-body {
  padding: 16px 18px 18px;
}

.danger-card {
  border: 1px solid #fecaca;
  border-radius: 14px;
  background: #fff7f7;
}

.danger-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.danger-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #991b1b;
}

.danger-subtitle {
  margin: 6px 0 0;
  font-size: 12px;
  color: #7f1d1d;
}

/* Make ContactsForm headings match the card */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}

:deep(.text-n-slate-11),
:deep(.text-n-slate-10) {
  color: #4b5563 !important;
}
</style>
